import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Wand2, Globe, Copy, ExternalLink, Rocket, Plus, Trash2, Monitor, Smartphone } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { SiteRenderer } from "../components/site/SiteRenderer";
import { api, errMsg } from "../lib/api";
import { PALETTES, FONTS, PHOTO_THEMES } from "../lib/siteThemes";

const LONG = ["body", "subheadline", "description"];
const SECTIONS = [["hero", "Top banner"], ["about", "About"], ["services", "Services"], ["gallery", "Gallery"], ["contact", "Contact"]];

const Fields = ({ obj, onChange, prefix }) => (
  <div className="space-y-3">
    {Object.entries(obj || {}).filter(([, v]) => typeof v === "string").map(([k, v]) => (
      <div key={k}>
        <Label className="text-xs capitalize text-muted-foreground">{k.replace(/_/g, " ")}</Label>
        {LONG.includes(k)
          ? <Textarea rows={k === "body" ? 5 : 2} className="mt-1" value={v} onChange={(e) => onChange({ ...obj, [k]: e.target.value })} data-testid={`${prefix}-${k}-input`} />
          : <Input className="mt-1" value={v} onChange={(e) => onChange({ ...obj, [k]: e.target.value })} data-testid={`${prefix}-${k}-input`} />}
      </div>
    ))}
  </div>
);

const SectionEditor = ({ k, sec, setSec, onRegen }) => {
  const [ins, setIns] = useState("");
  const [busy, setBusy] = useState(false);
  const regen = async () => { setBusy(true); await onRegen(k, ins); setBusy(false); setIns(""); };
  return (
    <div className="space-y-4">
      <Fields obj={sec} prefix={`edit-${k}`} onChange={setSec} />
      {k === "services" && (
        <div className="space-y-3">
          {(sec.items || []).map((it, i) => (
            <div key={i} className="rounded-xl border p-3" data-testid={`service-item-${i}`}>
              <div className="flex gap-2">
                <Input value={it.name} onChange={(e) => setSec({ ...sec, items: sec.items.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)) })} data-testid={`service-item-name-${i}`} />
                <Button size="icon" variant="ghost" onClick={() => setSec({ ...sec, items: sec.items.filter((_, j) => j !== i) })} data-testid={`service-item-remove-${i}`}><Trash2 className="h-4 w-4" /></Button>
              </div>
              <Textarea rows={2} className="mt-2" value={it.description} onChange={(e) => setSec({ ...sec, items: sec.items.map((x, j) => (j === i ? { ...x, description: e.target.value } : x)) })} />
            </div>
          ))}
          <Button size="sm" variant="outline" onClick={() => setSec({ ...sec, items: [...(sec.items || []), { name: "New service", description: "Describe it here." }] })} data-testid="service-item-add"><Plus className="mr-1 h-4 w-4" />Add service</Button>
        </div>
      )}
      <div className="flex gap-2 rounded-xl bg-accent p-2">
        <Input placeholder="Tell the AI what to change (optional)" value={ins} onChange={(e) => setIns(e.target.value)} className="bg-white" data-testid={`regen-instruction-${k}`} />
        <Button onClick={regen} disabled={busy} data-testid={`regen-section-${k}`}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Wand2 className="mr-1 h-4 w-4" />Rewrite</>}</Button>
      </div>
    </div>
  );
};

const PublishPanel = ({ site, save, setSite }) => {
  const [slug, setSlug] = useState(site.slug);
  const [domains, setDomains] = useState([]);
  useEffect(() => { api.get("/me/overview").then((r) => setDomains(r.data.domains)).catch(() => {}); }, []);
  const url = `${window.location.origin}/s/${site.slug}`;
  const act = async (fn, msg) => { try { setSite((await fn()).data); if (msg) toast.success(msg); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="space-y-5" data-testid="publish-panel">
      <div className="flex items-center justify-between rounded-2xl border p-4">
        <div><p className="font-bold">{site.status === "published" ? "Live on the internet" : "Not published yet"}</p><p className="text-xs text-muted-foreground">Free hosting, forever.</p></div>
        <Switch checked={site.status === "published"} data-testid="publish-switch"
          onCheckedChange={(v) => act(() => api.post(`/sites/${site.id}/publish`, { published: v }), v ? "Your website is live!" : "Website unpublished")} />
      </div>
      <div>
        <Label>Free web address</Label>
        <div className="mt-1 flex items-center gap-1 rounded-xl border bg-muted px-3 text-sm">
          <span className="max-w-[45%] truncate text-muted-foreground">{window.location.host}/s/</span>
          <input className="h-10 min-w-0 flex-1 bg-transparent font-semibold outline-none" value={slug} onChange={(e) => setSlug(e.target.value)} data-testid="site-slug-input" />
          {slug !== site.slug && <Button size="sm" onClick={() => save({ slug })} data-testid="site-slug-save">Save</Button>}
        </div>
        {site.status === "published" && (
          <div className="mt-2 flex gap-2">
            <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(url); toast.success("Link copied"); }} data-testid="copy-site-link"><Copy className="mr-1 h-3.5 w-3.5" />Copy link</Button>
            <a href={url} target="_blank" rel="noreferrer"><Button size="sm" variant="outline" data-testid="open-site-link"><ExternalLink className="mr-1 h-3.5 w-3.5" />Open</Button></a>
          </div>
        )}
      </div>
      <div className="rounded-2xl bg-gradient-to-br from-[#F0326E] to-[#FF7A45] p-5 text-white" data-testid="domain-upsell">
        <Globe className="h-6 w-6" />
        <p className="mt-2 font-display text-lg font-bold">Make it yours with a domain</p>
        {domains.length ? (
          <div className="mt-3 space-y-2">
            <Select value={site.custom_domain || "none"} onValueChange={(v) => act(() => api.post(`/sites/${site.id}/domain`, { domain: v === "none" ? null : v }), v === "none" ? "Domain disconnected" : `${v} connected`)}>
              <SelectTrigger className="bg-white text-foreground" data-testid="connect-domain-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="none">No domain</SelectItem>{domains.map((d) => <SelectItem key={d.id} value={d.domain}>{d.domain}</SelectItem>)}</SelectContent>
            </Select>
            {site.custom_domain && <p className="text-xs text-white/85">Connected. It goes live on {site.custom_domain} once its DNS points to HostAfricair.</p>}
          </div>
        ) : <p className="mt-1 text-sm text-white/85">Get {site.slug}.co.za or .africa. It's free for a year with any internet package.</p>}
        <Link to={`/domains?q=${site.slug}`}><Button size="sm" className="mt-3 bg-white text-primary hover:bg-white/90" data-testid="find-domain-btn">Find a domain</Button></Link>
      </div>
    </div>
  );
};

export default function SiteEditor() {
  const { id } = useParams();
  const [site, setSite] = useState(null);
  const [device, setDevice] = useState("desktop");
  const [dirty, setDirty] = useState(false);
  useEffect(() => { api.get(`/sites/${id}`).then((r) => setSite(r.data)); }, [id]);

  const save = useCallback(async (patch) => {
    try { setSite((await api.put(`/sites/${id}`, patch)).data); setDirty(false); toast.success("Saved"); } catch (e) { toast.error(errMsg(e)); }
  }, [id]);
  const setSection = (k, v) => { setSite({ ...site, sections: { ...site.sections, [k]: v } }); setDirty(true); };
  const regen = async (k, instruction) => {
    try { setSite((await api.post(`/sites/${id}/sections/${k}/regenerate`, { instruction })).data); toast.success("Section rewritten by AI"); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!site) return <div className="grid min-h-[60vh] place-items-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  return (
    <div className="grid min-h-[calc(100vh-100px)] overflow-x-hidden lg:grid-cols-[400px_1fr]" data-testid="site-editor">
      <aside className="min-w-0 space-y-5 overflow-y-auto overflow-x-hidden border-r bg-white p-5 lg:max-h-[calc(100vh-100px)]">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-primary">Website editor</p>
          <Input className="mt-1 border-0 px-0 font-display text-2xl font-extrabold shadow-none focus-visible:ring-0" value={site.business_name}
            onChange={(e) => { setSite({ ...site, business_name: e.target.value }); setDirty(true); }} data-testid="site-name-input" />
          <Input className="mt-1" value={site.tagline} onChange={(e) => { setSite({ ...site, tagline: e.target.value }); setDirty(true); }} placeholder="Tagline" data-testid="site-tagline-input" />
        </div>
        <Button className="w-full rounded-full" disabled={!dirty} data-testid="site-save-btn"
          onClick={() => save({ business_name: site.business_name, tagline: site.tagline, sections: site.sections })}>{dirty ? "Save changes" : "All changes saved"}</Button>
        <div>
          <Label className="text-xs text-muted-foreground">Colours</Label>
          <div className="mt-2 flex flex-wrap gap-2">{Object.entries(PALETTES).map(([k, p]) => (
            <button key={k} onClick={() => save({ theme: { ...site.theme, palette: k } })} title={p.label} data-testid={`palette-${k}`}
              className={`h-9 w-9 rounded-full border-4 transition-transform hover:scale-110 ${site.theme.palette === k ? "border-foreground" : "border-white shadow"}`}
              style={{ background: `linear-gradient(135deg, ${p.primary} 50%, ${p.accent} 50%)` }} />))}
          </div>
          <div className="mt-3 flex gap-2">{Object.entries(FONTS).map(([k, f]) => (
            <button key={k} onClick={() => save({ theme: { ...site.theme, font: k } })} data-testid={`font-${k}`} style={{ fontFamily: f.heading }}
              className={`rounded-full border px-3 py-1.5 text-sm ${site.theme.font === k ? "border-primary bg-accent text-primary" : ""}`}>{f.label}</button>))}
          </div>
          <Select value={site.category} onValueChange={(v) => save({ category: v })}>
            <SelectTrigger className="mt-3" data-testid="photo-theme-select"><SelectValue /></SelectTrigger>
            <SelectContent>{PHOTO_THEMES.map((t) => <SelectItem key={t} value={t} className="capitalize">Photos: {t}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <Accordion type="single" collapsible defaultValue="hero">
          {SECTIONS.map(([k, label]) => (
            <AccordionItem key={k} value={k}>
              <AccordionTrigger className="font-bold" data-testid={`section-toggle-${k}`}>{label}</AccordionTrigger>
              <AccordionContent><SectionEditor k={k} sec={site.sections[k] || {}} setSec={(v) => setSection(k, v)} onRegen={regen} /></AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
        <PublishPanel site={site} save={save} setSite={setSite} />
      </aside>
      <main className="bg-muted/60 p-4 lg:p-6">
        <div className="mb-3 flex items-center gap-2">
          {[["desktop", Monitor], ["mobile", Smartphone]].map(([d, I]) => (
            <button key={d} onClick={() => setDevice(d)} data-testid={`preview-${d}`} className={`rounded-full p-2 ${device === d ? "bg-primary text-white" : "bg-white"}`}><I className="h-4 w-4" /></button>))}
          <span className="ml-2 flex items-center gap-1 text-xs font-semibold text-muted-foreground"><Rocket className="h-3.5 w-3.5" />Live preview</span>
        </div>
        <div className={`mx-auto overflow-hidden rounded-2xl border bg-white shadow-xl transition-[max-width] duration-300 ${device === "mobile" ? "max-w-[390px]" : "max-w-full"}`}>
          <div className="max-h-[calc(100vh-190px)] overflow-y-auto"><SiteRenderer site={site} compact={device === "mobile"} /></div>
        </div>
      </main>
    </div>
  );
}
